import React, { useState, useEffect, useRef } from "react";

// OBTO brand kit, dark theme. gold/goldLight = violet-text on dark, teal = the live violet dot, fill = deep violet with white text.
const C = {
  black: "#121214", dark: "#1A1A1E", charcoal: "#1E1E22", gunmetal: "#2E2E34",
  smoke: "#3A3A42", gray: "#9A98A3", silver: "#9A98A3", light: "#F5F3EF",
  offwhite: "#F5F3EF", white: "#FFFFFF", gold: "#B4AAFF", goldDark: "#7C6CF2",
  goldLight: "#B4AAFF", teal: "#7C6CF2", tealDark: "#5345C9",
  fill: "#5345C9", onFill: "#FFFFFF",
};

function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      ...style, opacity: vis ? 1 : 0,
      transform: vis ? "translateY(0)" : "translateY(40px)",
      transition: `all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s`,
    }}>{children}</div>
  );
}

export default function AboutUs() {
  return (
    <div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&family=DM+Mono:wght@400;500&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { background: ${C.black}; color: ${C.white}; font-family: 'Figtree', system-ui, sans-serif; overflow-x: hidden; }
      `}</style>
      <main style={{ minHeight: "100vh", padding: "200px 48px 120px", maxWidth: "1200px", margin: "0 auto" }}>
        <Reveal>
          <span style={{ fontSize: "12px", color: C.gold, letterSpacing: "4px", fontWeight: 500 }}>ABOUT OBTO</span>
          <h1 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "72px", fontWeight: 700, lineHeight: 1.05, maxWidth: "900px", margin: "16px 0 32px" }}>
            Redefining software<br /><span style={{ color: C.gold, fontStyle: "italic" }}>from conversation to cloud.</span>
          </h1>
          <p style={{ fontSize: "18px", color: C.silver, lineHeight: 1.7, maxWidth: "560px", fontWeight: 300 }}>
            Founded in 2025 by engineers frustrated with fragmented tools. OBTO bridges AI conversations to production apps—no code editors, no deploys, just speech-to-software.
          </p>
        </Reveal>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", marginTop: "140px" }}>
          <Reveal delay={0.2}>
            <div>
              <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "40px", fontWeight: 600, marginBottom: "24px" }}>Our Story</h2>
              <p style={{ fontSize: "16px", color: C.silver, lineHeight: 1.8, marginBottom: "20px" }}>
                We saw AI agents trapped in chat windows, devs trapped in IDEs. MCP unlocked the path: universal protocol for AI to wield deployment tools directly.
              </p>
              <p style={{ fontSize: "16px", color: C.silver, lineHeight: 1.8 }}>
                Now, 10k+ apps live on OBTO—from indie landing pages to enterprise dashboards. All born from prompts.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div style={{ background: C.charcoal, padding: "48px", border: `1px solid ${C.smoke}`, borderRadius: "12px" }}>
              <h3 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "24px" }}>Key Milestones</h3>
              <ul style={{ listStyle: "none", color: C.silver, fontSize: "15px" }}>
                <li style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}><span style={{ color: C.gold, fontSize: "20px" }}>●</span>2025: MCP server launch</li>
                <li style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}><span style={{ color: C.gold, fontSize: "20px" }}>●</span>2026: 10k apps deployed</li>
                <li style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}><span style={{ color: C.gold, fontSize: "20px" }}>●</span>Enterprise self-hosting</li>
                <li style={{ display: "flex", alignItems: "center", gap: "12px" }}><span style={{ color: C.gold, fontSize: "20px" }}>●</span>Full-stack AI builder</li>
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.6}>
          <div style={{ marginTop: "140px", textAlign: "center" }}>
            <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "52px", fontWeight: 600, marginBottom: "64px" }}>The Team</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr)", gap: "32px" }}>
              {[
                { img: "👨‍💻", name: "Alex Rivera", role: "Founder & CEO" },
                { img: "👩‍🔬", name: "Jordan Lee", role: "CTO & Architect" },
                { img: "🧠", name: "Sam Patel", role: "AI Lead" },
                { img: "🎨", name: "Taylor Kim", role: "Product Design" },
              ].map((member, i) => (
                <div key={i} style={{ padding: "48px 32px", background: C.charcoal, border: `1px solid ${C.smoke}`, borderRadius: "12px", transition: "all 0.3s" }}
                     onMouseEnter={e => e.currentTarget.style.borderColor = C.gold}
                     onMouseLeave={e => e.currentTarget.style.borderColor = C.smoke}>
                  <div style={{ fontSize: "72px", marginBottom: "24px" }}>{member.img}</div>
                  <h3 style={{ fontSize: "22px", fontWeight: 600, marginBottom: "8px" }}>{member.name}</h3>
                  <p style={{ fontSize: "15px", color: C.silver }}>{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div style={{ marginTop: "140px", padding: "80px 0", borderTop: `1px solid ${C.smoke}` }}>
          <Reveal>
            <h2 style={{ fontFamily: "'Figtree', system-ui, sans-serif", fontSize: "40px", fontWeight: 600, textAlign: "center", marginBottom: "72px" }}>Our Values</h2>
          </Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr)", gap: "32px" }}>
            {[
              { title: "Transparent", desc: "Glass-box AI. Inspect, audit, own every artifact." },
              { title: "Universal", desc: "Any MCP LLM. Open protocol. No vendor lock-in." },
              { title: "Accessible", desc: "Plain English prompts build production apps." },
              { title: "Reliable", desc: "Kubernetes scale. Zero-downtime deploys." },
            ].map((value, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{ padding: "40px 32px", borderLeft: `4px solid ${C.gold}`, background: C.charcoal, borderRadius: "8px" }}>
                  <h3 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "16px" }}>{value.title}</h3>
                  <p style={{ fontSize: "15px", color: C.silver, lineHeight: 1.7 }}>{value.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}